import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import Icon from 'dpl/common/components/Icon';
import Button from 'dpl/common/design_system/Button';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { FILTER_SIMILAR_BREEDS, PAGE, SORT } from 'dpl/constants/query_params';
import GenericMenu from 'dpl/path_to_payment/components/GenericMenu';
import useSearchResultsSort from 'dpl/puppy_search_results/hooks/useSearchResultsSort';
import {
  RESULTS_TYPES,
  SORT_BEST_MATCH,
  SORT_OPTIONS
} from 'dpl/puppy_search_results/utils/constants';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import SortOptionButton from './SortOptionButton';

const IS_LT_MD = isLessThanBreakpoint('md');

const TITLE = 'Sort by';
const MOBILE_LABEL_MIN_WIDTH = '70px';
const MOBILE_CHEVRON_SIZE = '24px';

export default function SortButton({ resultsType }: { resultsType: string }) {
  const dispatch = useDispatch();
  const { isSortVisible, activeSort, selectedOption } = useSearchResultsSort();
  const [isOpen, setIsOpen] = useState(false);

  if (!isSortVisible) {
    return null;
  }

  const label = selectedOption?.label ?? 'Sort';

  const analytics = {
    view:
      resultsType === RESULTS_TYPES.PUPPIES
        ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
        : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS
  };

  function handleOpen() {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      ...analytics,
      button_cta: 'Sort',
      view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_SORT_BUTTON,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_HEADER,
      sort: activeSort
    });
    setIsOpen(true);
  }

  function handleSelect(sort: string) {
    setIsOpen(false);

    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      ...analytics,
      button_cta: SORT_OPTIONS.find(option => option.value === sort)?.label,
      view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_SORT_OPTION,
      view_feature_location: IS_LT_MD
        ? ANALYTICS_VIEW_FEATURE_LOCATIONS.BOTTOM_SHEET
        : ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_HEADER,
      sort,
      previous_sort: activeSort
    });

    // Against the picked sort, not `activeSort`: picking best match from the
    // untouched default still writes it, so the button can show it.
    if (sort === selectedOption?.value) {
      return;
    }

    if (sort === SORT_BEST_MATCH) {
      // Best match is still a mixed feed, so similar breeds stays.
      dispatch(pushParams({ [SORT]: sort }, { hash: true }));
    } else {
      // The server never mixes a sorted feed, so similar breeds goes with it.
      // `undefined` drops the key, keeping this to one history entry.
      dispatch(
        pushParams(
          { [SORT]: sort, [FILTER_SIMILAR_BREEDS]: undefined },
          { hash: true }
        )
      );
    }

    dispatch(resetParams(PAGE));
  }

  const toggleButton = (
    <Button
      variant={isOpen ? 'outlineBlue' : 'outline'}
      size="icon"
      className="flex items-center gap-2 bg-white font-14"
      onClick={handleOpen}
    >
      <span className="secondary pl2">{label}</span>
      <Icon
        name={isOpen ? 'fetch-chevron-up' : 'fetch-chevron-down'}
        width="24px"
        height="24px"
      />
    </Button>
  );

  function renderOptions(variant: 'dropdown' | 'sheet') {
    return SORT_OPTIONS.map(option => (
      <SortOptionButton
        key={option.value}
        option={option}
        isSelected={option.value === activeSort}
        onChange={handleSelect}
        variant={variant}
      />
    ));
  }

  if (IS_LT_MD) {
    return (
      <>
        <button
          type="button"
          onClick={handleOpen}
          // Takes the room the location leaves, up to the full label. A long
          // city shortens the label to the floor, then truncates itself. The
          // floor sits here because a `nowrap` label alone won't let the
          // button shrink; "Sort" is narrower than it, so it goes without.
          className="flex flex-1 items-center font-14 fw-medium lh-1 secondary hide-button-press"
          style={{
            maxWidth: 'max-content',
            minWidth: selectedOption
              ? `calc(${MOBILE_LABEL_MIN_WIDTH} + ${MOBILE_CHEVRON_SIZE})`
              : undefined
          }}
        >
          {/* `truncate` hides overflow, so the line box has to clear the
              font's ascent and descent: `lh-1` alone clips them. */}
          <span className="truncate lh-title">{label}</span>
          <Icon
            name={isOpen ? 'fetch-chevron-up' : 'fetch-chevron-down'}
            className="tertiary"
            width={MOBILE_CHEVRON_SIZE}
            height={MOBILE_CHEVRON_SIZE}
          />
        </button>
        <FixedTooltipPortal
          isVisible={isOpen}
          onCloseClick={() => setIsOpen(false)}
          className="relative pt5"
          // FixedTooltip pads its content; the options' highlight runs edge to
          // edge, so drop that padding here.
          style={{ maxHeight: '90vh', paddingLeft: 0, paddingRight: 0 }}
          shouldHideCloseButton
        >
          <p className="font-24 font-serif fw-medium dib mb4 ph5">{TITLE}</p>
          {renderOptions('sheet')}
        </FixedTooltipPortal>
      </>
    );
  }

  return (
    <div className="relative">
      {toggleButton}
      {isOpen && (
        <GenericMenu
          setIsMenuOpen={setIsOpen}
          className="ExposedFilterDropdownMenu__container mt2 z-max absolute right-0 overflow-hidden"
          // GenericMenu pads its content; the options' highlight runs edge to
          // edge, so drop that padding here.
          style={{ paddingLeft: 0, paddingRight: 0 }}
          closeOnClickOutside
        >
          <p className="pa2 font-14 font-sans fw-normal lh-body tertiary">
            {TITLE}
          </p>
          {renderOptions('dropdown')}
        </GenericMenu>
      )}
    </div>
  );
}
